import { I18nProvider } from "@lingui/react";
import { DirectionProvider } from "@radix-ui/react-direction";
import React from "react";
import { AgentsPage } from "./components/AgentsPage";
import { CallersPage } from "./components/CallersPage";
import { CallsPage } from "./components/CallsPage";
import { Dashboard } from "./components/Dashboard";
import { LoginPage } from "./components/LoginPage";
import { Navigation } from "./components/Navigation";
import { PlaybookRunsPage } from "./components/PlaybookRunsPage";
import { RefreshVersionBanner } from "./components/RefreshVersionBanner";
import { RegisterPage } from "./components/RegisterPage";
import { ServicesPage } from "./components/ServicesPage";
import { SettingsPage } from "./components/SettingsPage";
import { Skeleton } from "./components/ui/skeleton";
import { Toaster } from "./components/ui/sonner";
import { DataProvider, useData } from "./contexts/DataContext";
import { type Page, useRouter } from "./hooks/use-router";
import { activateLocale, i18n } from "./i18n";

// type Page removed as it is imported

function AppContent() {
	const {
		page: currentPage,
		callerId,
		navigateToCaller,
		navigateToCallerCall,
		callId: selectedCallId,
		agentName: selectedAgentName,
		setPage: setCurrentPage,
		navigateToCall,
		navigateToAgent,
	} = useRouter();

	const { user, isLoggedIn, logout, loading, language } = useData();

	// Apply RTL when Hebrew is selected
	React.useEffect(() => {
		activateLocale(language);
		document.documentElement.dir = language === "he" ? "rtl" : "ltr";
		document.documentElement.lang = language;
		document.title = "Provoize App";
	}, [language]);

	const handleLogout = () => {
		logout();
		setCurrentPage("dashboard");
	};

	const renderPage = () => {
		switch (currentPage) {
			case "dashboard":
				return (
					<Dashboard
						onNavigateToCall={navigateToCall}
						onNavigateToAgent={navigateToAgent}
					/>
				);
			case "callers":
				return (
					<CallersPage
						key={user?.companyId}
						callerId={callerId}
						selectedCallId={selectedCallId}
						onCallSelect={navigateToCallerCall}
						onCallerSelect={navigateToCaller}
					/>
				);
			case "calls":
				return (
					<CallsPage
						selectedCallId={selectedCallId}
						onCallSelect={navigateToCall}
					/>
				);
			case "outbound-playbooks":
				return (
					<AgentsPage
						initialAgentName={selectedAgentName}
						onAgentChange={() => navigateToAgent(null)}
					/>
				);
			case "playbook-runs":
				return <PlaybookRunsPage />;
			case "services":
				return <ServicesPage />;
			case "agents":
				return (
					<AgentsPage
						initialAgentName={selectedAgentName}
						onAgentChange={() => navigateToAgent(null)}
					/>
				);
			case "settings":
				return <SettingsPage />;
			default:
				return (
					<Dashboard
						onNavigateToCall={navigateToCall}
						onNavigateToAgent={navigateToAgent}
					/>
				);
		}
	};

	return (
		<DirectionProvider dir={language === "he" ? "rtl" : "ltr"}>
			<I18nProvider i18n={i18n}>
				{loading ? (
					<div
						className="min-h-screen bg-background"
						role="status"
						aria-label={i18n._("loading")}
						aria-busy="true"
					>
						<div className="border-b border-border bg-card px-4 py-3">
							<div className="mx-auto flex max-w-7xl items-center gap-4">
								<Skeleton className="h-8 w-28" />
								<Skeleton className="h-8 flex-1" />
								<Skeleton className="h-8 w-8 rounded-full" />
							</div>
						</div>
						<main className="mx-auto max-w-7xl space-y-6 px-4 py-6">
							<div className="space-y-2">
								<Skeleton className="h-9 w-52" />
								<Skeleton className="h-5 w-full max-w-md" />
							</div>
							<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
								{Array.from({ length: 4 }, (_, index) => (
									<Skeleton key={index} className="h-24" />
								))}
							</div>
							<Skeleton className="h-80" />
						</main>
					</div>
				) : !isLoggedIn ? (
					currentPage === "register" ? (
						<RegisterPage />
					) : (
						<LoginPage />
					)
				) : (
					<div
						key={`${user?.uid}:${user?.companyId}`}
						className="min-h-screen bg-background"
					>
						<Navigation
							currentPage={currentPage}
							onPageChange={(page) => setCurrentPage(page as Page)}
							user={user}
							onLogout={handleLogout}
						/>
						<main className="py-6">{renderPage()}</main>
						<RefreshVersionBanner />
						<Toaster />
					</div>
				)}
			</I18nProvider>
		</DirectionProvider>
	);
}

function App() {
	return (
		<DataProvider>
			<AppContent />
		</DataProvider>
	);
}

export default App;
